import { useQuery } from '@tanstack/react-query';
import type { InternalNetworkAdEntityKind } from '@terra/ui-graphs/internal-network';
import type { InternalNetworkAdEntityRelationshipDto } from '@terra/shared-contracts-internal-network';
import { terraProxyApiClient } from '../../clients/testerClient';
import { SURFACE_ASSETS_KEY } from './useSurfaceAssets';

export function useCanonicalInternalNetworkAsset({
  networkId,
  assetId,
}: {
  networkId: string | null;
  assetId: string | null;
}) {
  const query = useQuery({
    queryKey: [
      SURFACE_ASSETS_KEY,
      'canonical-internal-detail',
      networkId,
      assetId,
    ],
    queryFn: () =>
      terraProxyApiClient.getCanonicalInternalNetworkAsset({
        networkId: networkId ?? '',
        assetId: assetId ?? '',
      }),
    enabled: Boolean(networkId) && Boolean(assetId),
  });
  const relationships = useQuery({
    queryKey: [
      SURFACE_ASSETS_KEY,
      'canonical-internal-relationships',
      networkId,
      assetId,
    ],
    queryFn: () =>
      terraProxyApiClient.getCanonicalInternalNetworkAssetRelationships({
        networkId: networkId ?? '',
        assetId: assetId ?? '',
      }),
    enabled: Boolean(networkId) && Boolean(assetId),
  });

  return {
    asset: query.data ?? null,
    relationships: relationships.data?.items ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useInternalNetworkAdEntity({
  networkId,
  nodeId,
}: {
  networkId: string | null;
  nodeId: string | null;
}) {
  const query = useQuery({
    queryKey: [SURFACE_ASSETS_KEY, 'internal-detail', networkId, nodeId],
    queryFn: () =>
      terraProxyApiClient.getInternalNetworkAdEntity({
        networkId: networkId ?? '',
        nodeId: nodeId ?? '',
      }),
    enabled: Boolean(networkId) && Boolean(nodeId),
  });

  return {
    entity: query.data?.entity ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

export function useInternalNetworkAdEntityRelationships({
  networkId,
  nodeId,
}: {
  networkId: string | null;
  nodeId: string | null;
}) {
  const query = useQuery({
    queryKey: [SURFACE_ASSETS_KEY, 'internal-relationships', networkId, nodeId],
    queryFn: () =>
      terraProxyApiClient.getInternalNetworkAdEntityRelationships({
        networkId: networkId ?? '',
        nodeId: nodeId ?? '',
      }),
    enabled: Boolean(networkId) && Boolean(nodeId),
  });

  const emptyRelationships: InternalNetworkAdEntityRelationshipDto[] = [];

  return {
    relationships: query.data?.relationships ?? emptyRelationships,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

export function useExternalNetworkAssetDetail({
  surfaceId,
  entityKind,
  entityId,
}: {
  surfaceId: string | null;
  entityKind: string | null;
  entityId: string | null;
}) {
  const query = useQuery({
    queryKey: [
      SURFACE_ASSETS_KEY,
      'external-detail',
      surfaceId,
      entityKind,
      entityId,
    ],
    queryFn: async () => {
      if (!surfaceId || !entityId || !entityKind) {
        return null;
      }

      switch (entityKind) {
        case 'DOMAIN':
          return terraProxyApiClient.getSurfaceDomain({
            surfaceId,
            domainId: entityId,
          });
        case 'HOST':
          return terraProxyApiClient.getSurfaceHost({
            surfaceId,
            hostId: entityId,
          });
        case 'GATEWAY':
          return terraProxyApiClient.getSurfaceGateway({
            surfaceId,
            gatewayId: entityId,
          });
        case 'NETWORK_SERVICE':
          return terraProxyApiClient.getSurfaceService({
            surfaceId,
            serviceId: entityId,
          });
        case 'NETBLOCK':
          return terraProxyApiClient.getSurfaceNetblock({
            surfaceId,
            netblockId: entityId,
          });
        case 'SOFTWARE':
          return terraProxyApiClient.getSurfaceSoftware({
            surfaceId,
            softwareId: entityId,
          });
        case 'STORAGE_SERVICE':
          return terraProxyApiClient.getSurfaceStorageService({
            surfaceId,
            storageServiceId: entityId,
          });
        default:
          return null;
      }
    },
    enabled: Boolean(surfaceId) && Boolean(entityId) && Boolean(entityKind),
  });

  return {
    asset: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

export type { InternalNetworkAdEntityKind };
